import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import useChat from './useChat';
import useCurrentUser from './useCurrentUser';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 159/193/217 — per-user, PER-THREAD read watermark for the append-only chat. The
// messages are immutable, so read-state is tracked separately: GET the caller's last-read
// time for the thread, POST to mark it read up to now. Unread = messages after that time
// NOT authored by the current user. `threadKey` selects the thread (undefined = the
// company↔accountant thread, a project id = project chat, TEAM_THREAD_KEY = team channel).
const useChatRead = (threadKey?: string) => {
  const queryClient = useQueryClient();
  const queryKey = threadKey ? ['chatRead', threadKey] : ['chatRead'];
  const params = threadKey ? { threadKey } : {};

  const query = useQuery(
    queryKey,
    async (): Promise<{ lastReadDate: string | null }> => {
      const { data } = await axios.get(`${apiUrl()}chatread`, { params });
      return data;
    },
    { refetchOnWindowFocus: true },
  );

  const mark = useMutation(() => axios.post(`${apiUrl()}chatread`, null, { params }), {
    onSuccess: () => queryClient.invalidateQueries(queryKey),
  });

  return {
    lastReadDate: query.data?.lastReadDate ?? null,
    markRead: () => mark.mutateAsync(),
    isMarking: mark.isLoading,
  };
};

// item 159 — the unread count + per-message unread test, combining the polled messages
// with the user's read watermark. Reused by the chat panel (styling + auto-mark) and the
// chat entry badge (and item 158's dashboard surface). Pass the same `threadKey` as the
// panel so unread state is per thread.
export const useChatUnread = (threadKey?: string) => {
  const { messages } = useChat(threadKey);
  const { lastReadDate, markRead } = useChatRead(threadKey);
  const { user } = useCurrentUser();
  const readMs = lastReadDate ? new Date(lastReadDate).getTime() : 0;

  const isUnread = (m: { authorUserId: string; createdDate: string }): boolean => (
    m.authorUserId !== user?.id && new Date(m.createdDate).getTime() > readMs
  );

  const unreadCount = messages.filter(isUnread).length;
  return {
    unreadCount, isUnread, markRead, lastReadDate,
  };
};

export default useChatRead;
